import React, { useMemo, useState } from "react";
import { Calculator, Zap, IndianRupee, Sun } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useContent } from "../contexts/ContentContext";

export default function SolarCalculator({ onEnquire }) {
  const { content } = useContent();
  const { indianStates } = content;
  const [bill, setBill] = useState("5000");
  const [state, setState] = useState("Haryana");
  const [area, setArea] = useState("500");

  const result = useMemo(() => {
    const b = Math.max(0, parseFloat(bill) || 0);
    const a = Math.max(0, parseFloat(area) || 0);
    // Rough estimate: 1 kW saves ~ Rs 800/month
    const sizeFromBill = Math.min(10, Math.max(1, Math.round(b / 800)));
    const sizeFromArea = Math.min(10, Math.max(1, Math.floor(a / 100)));
    const size = Math.min(sizeFromBill, sizeFromArea);
    const monthlySavings = size * 800;
    const yearlySavings = monthlySavings * 12;
    const systemCost = size * 65000;
    const subsidy = Math.min(78000, size * 18000);
    const netCost = Math.max(0, systemCost - subsidy);
    const payback = (netCost / yearlySavings).toFixed(1);
    const co2 = (size * 1.5).toFixed(1);
    return { size, monthlySavings, yearlySavings, systemCost, subsidy, netCost, payback, co2 };
  }, [bill, area]);

  return (
    <section id="calculator" className="py-20 lg:py-28 bg-[#0a3d38] relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-orange-500/20 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center max-w-2xl mx-auto text-white">
          <div className="inline-block px-3 py-1 rounded-full bg-orange-500/20 text-orange-300 text-xs font-semibold tracking-wider mb-4">
            INSTANT ESTIMATE
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold">
            Solar <span className="text-orange-400">Savings Calculator</span>
          </h2>
          <p className="text-slate-300 mt-4">
            Find out how much you can save in under 30 seconds.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          {/* Inputs */}
          <div className="lg:col-span-3 bg-white rounded-2xl p-6 lg:p-8 shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="w-5 h-5 text-orange-500" />
              <h3 className="text-xl font-bold text-slate-900">Tell us about your home</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Monthly Electricity Bill (₹)</Label>
                <Input
                  type="number"
                  value={bill}
                  onChange={(e) => setBill(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="e.g. 5000"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-semibold text-sm">Available Roof Area (sq ft)</Label>
                <Input
                  type="number"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="mt-1.5 h-11"
                  placeholder="e.g. 500"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700 font-semibold text-sm">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger className="mt-1.5 h-11">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {indianStates.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              onClick={onEnquire}
              className="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white h-12 font-semibold"
            >
              Get Detailed Quote
            </Button>
          </div>

          {/* Results */}
          <div className="lg:col-span-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 lg:p-8 text-white shadow-xl">
            <div className="flex items-center gap-2 mb-6">
              <Sun className="w-5 h-5" />
              <h3 className="text-xl font-bold">Your Solar Estimate</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-white/20">
                <span className="text-orange-50 text-sm">Recommended system</span>
                <span className="font-extrabold text-xl">{result.size} kW</span>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-white/20">
                <span className="text-orange-50 text-sm flex items-center gap-1.5"><Zap className="w-4 h-4" /> Monthly savings</span>
                <span className="font-extrabold text-xl">₹{result.monthlySavings.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-white/20">
                <span className="text-orange-50 text-sm flex items-center gap-1.5"><IndianRupee className="w-4 h-4" /> System cost</span>
                <span className="font-bold">₹{result.systemCost.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-white/20">
                <span className="text-orange-50 text-sm">PM Surya Ghar subsidy</span>
                <span className="font-bold">- ₹{result.subsidy.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex items-center justify-between pb-3 border-b border-white/20">
                <span className="text-orange-50 text-sm">Net cost</span>
                <span className="font-extrabold">₹{result.netCost.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-orange-50 text-sm">Payback period</span>
                <span className="font-extrabold">{result.payback} years</span>
              </div>
              <div className="mt-4 px-3 py-2 rounded-lg bg-white/15 text-sm">
                You&apos;ll offset ~{result.co2} tonnes CO₂ every year.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
